'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { useReadContract, useWaitForTransactionReceipt } from 'wagmi';
import { LRSNODE_CONTRACT_CONFIG, LRSNODE_V3_CONTRACT_CONFIG } from '@/configs/lrsnode.config';
import { useNodeActions } from '@/hooks/use-node-actions';
import { useNodeActionsV3 } from '@/hooks/use-node-actions-v3';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/sheet';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { RiLoader4Line, RiCheckLine, RiCloseLine } from '@remixicon/react';

interface CreateReferCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  useV3?: boolean;
}

export default function CreateReferCodeModal({ isOpen, onClose, onSuccess, useV3 = false }: CreateReferCodeModalProps) {
  const [referCode, setReferCode] = useState('');
  const [error, setError] = useState('');
  const [txHash, setTxHash] = useState<`0x${string}` | undefined>(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Select hooks and config based on useV3 prop
  const { handleCreateReferCode: handleCreateReferCodeV2 } = useNodeActions();
  const { handleCreateReferCode: handleCreateReferCodeV3 } = useNodeActionsV3();
  const handleCreateReferCode = useV3 ? handleCreateReferCodeV3 : handleCreateReferCodeV2;
  
  // Memoize contract config to prevent infinite loops
  const contractConfig = useMemo(() => {
    return useV3 ? LRSNODE_V3_CONTRACT_CONFIG : LRSNODE_CONTRACT_CONFIG;
  }, [useV3]);

  // Check if refer code exists
  const { data: existingAddress } = useReadContract({
    address: contractConfig.address,
    abi: contractConfig.abi,
    functionName: 'referCodeToAddress',
    args: [referCode],
    query: {
      enabled: referCode.length > 0,
    }
  });

  // Watch transaction status
  const { isLoading: isPending, isSuccess } = useWaitForTransactionReceipt({
    hash: txHash,
    query: {
      enabled: !!txHash,
    },
  });

  // Reset modal state when closed
  useEffect(() => {
    if (!isOpen) {
      setReferCode('');
      setError('');
      setTxHash(undefined);
      setIsProcessing(false);
    }
  }, [isOpen]);

  // Validate input in real-time
  useEffect(() => {
    if (referCode) {
      if (referCode.length < 4) {
        setError('Refer code must be at least 4 characters');
      } else if (referCode.length > 6) {
        setError('Refer code cannot be longer than 6 characters');
      } else {
        setError('');
      }
    } else {
      setError('');
    }
  }, [referCode]);

  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    if (!isSuccess || !txHash) return;
    onSuccessRef.current();
  }, [isSuccess, txHash]);

  const handleCreate = async () => {
    try {
      if (!referCode.trim()) {
        setError('Please enter a refer code');
        return;
      }

      if (referCode.length < 4 || referCode.length > 6) {
        setError('Refer code must be between 4 and 6 characters');
        return;
      }

      setError('');
      setIsProcessing(true);

      // Check if code is already taken
      if (existingAddress && existingAddress !== '0x0000000000000000000000000000000000000000') {
        setError('This refer code is already taken');
        setIsProcessing(false);
        return;
      }

      const tx = await handleCreateReferCode(referCode);
      setTxHash(tx.hash);

    } catch (err: any) {
      setError(err.message || 'Failed to create refer code');
      setIsProcessing(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow alphanumeric characters
    const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    setReferCode(value);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="text-[#FF8A3C]">Create Refer Code</SheetTitle>
          <SheetDescription>
            Create a unique refer code to start earning rewards from referrals.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-4 mx-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              Enter refer code (4-6 characters)
            </label>
            <Input
              type="text"
              value={referCode}
              onChange={handleInputChange}
              placeholder="Example: ABC123"
              disabled={isProcessing || isPending}
            />
            <div className="flex justify-between mt-1">
              <p className="text-muted-foreground text-xs">
                Only letters and numbers allowed
              </p>
              <p className={`text-xs ${
                referCode.length < 4 ? 'text-red-400' : 
                referCode.length > 6 ? 'text-red-400' : 
                'text-green-400'
              }`}>
                {referCode.length}/6 characters
              </p>
            </div>
          </div>

          {error && (
            <div className="text-red-500 text-sm bg-red-500/10 p-3 rounded-lg">{error}</div>
          )}

          {txHash && (
            <div className="py-4">
              {isPending && (
                <div className="flex flex-col items-center justify-center space-y-4">
                  <RiLoader4Line className="size-12 text-[#FF8A3C] animate-spin" />
                  <p className="text-center">Creating refer code...</p>
                  <p className="text-center text-sm text-muted-foreground">
                    Please wait while your transaction is being processed...
                  </p>
                </div>
              )}
              {isSuccess && (
                <div className="flex flex-col items-center justify-center space-y-4">
                  <div className="size-12 rounded-full bg-green-500/20 flex items-center justify-center">
                    <RiCheckLine className="size-6 text-green-500" />
                  </div>
                  <p className="text-center text-green-500">Refer code created successfully!</p>
                </div>
              )}

              <div className="mt-4 text-sm text-center">
                <a
                  href={`https://scan.larissa.network/tx/${txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FF8A3C] hover:text-[#FF8A3C]/80"
                >
                  View on Explorer →
                </a>
              </div>
            </div>
          )}

          <div className="flex space-x-3">
            <Button
              onClick={handleCreate}
              disabled={isProcessing || isPending || referCode.length < 4 || referCode.length > 6}
              className="flex-1 bg-[#FF8A3C] hover:bg-[#FF8A3C]/90"
            >
              {isProcessing || isPending ? (
                <div className="flex items-center justify-center space-x-2">
                  <RiLoader4Line className="size-4 animate-spin" />
                  <span>Processing...</span>
                </div>
              ) : (
                'Create'
              )}
            </Button>
            <Button
              onClick={onClose}
              disabled={isProcessing || isPending}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

